import { useCallback, useEffect, useRef, useState } from 'react'
import type { Step } from 'react-joyride'
import { useAuth } from '../contexts/AuthContext'
import { useRole } from '../contexts/RoleContext'
import { supabase } from '../lib/supabase'

export interface CoachOnboardingStep extends Step {
  target: string
  content: string
  title?: string
  disableBeacon?: boolean
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto'
}

export const useCoachOnboarding = () => {
  const { user } = useAuth()
  const { currentRole } = useRole()
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [loading, setLoading] = useState(true)

  // Define tour steps
  const steps: CoachOnboardingStep[] = [
    {
      target: 'body',
      content: "Let's take a quick tour to help you get started as a coach.",
      title: 'Welcome to supacoach! 🎉',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="invite-button"]',
      content:
        "Click here to invite athletes to your coaching program. There's a link you can share to sign up and connect with you.",
      title: 'Invite Athletes',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="add-self-link"]',
      content:
        'Want to test the platform? Click here to add yourself as an athlete. You can switch between coach and athlete views.',
      title: 'Test as an Athlete',
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="user-menu"]',
      content: 'Access your account settings, switch roles, and sign out from here.',
      title: 'User Menu',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: "That's it! You're ready to start coaching. Add some athletes to get started!",
      title: "You're All Set! 🚀",
      placement: 'center',
      disableBeacon: true,
    },
  ]

  // Check if user has completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.id) {
        setLoading(false)
        return
      }

      // Only show onboarding to coaches
      if (currentRole !== 'coach') {
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed_coach')
          .eq('id', user.id)
          .single()

        if (error) {
          console.error('Error checking onboarding status:', error)
          setLoading(false)
          return
        }

        // Start tour if onboarding not completed
        if (data && !data.onboarding_completed_coach) {
          setRun(true)
        }
      } catch (err) {
        console.error('Error in checkOnboardingStatus:', err)
      } finally {
        setLoading(false)
      }
    }

    checkOnboardingStatus()
  }, [user?.id, currentRole])

  // Mark onboarding as completed
  const completeOnboarding = useCallback(async () => {
    if (!user?.id) return

    try {
      const { error } = await supabase.from('profiles').update({ onboarding_completed_coach: true }).eq('id', user.id)

      if (error) {
        console.error('Error completing onboarding:', error)
      }
    } catch (err) {
      console.error('Error in completeOnboarding:', err)
    }
  }, [user?.id])

  // Reset onboarding (for testing or if user wants to see tour again)
  const resetOnboarding = useCallback(async () => {
    if (!user?.id) return

    try {
      const { error } = await supabase.from('profiles').update({ onboarding_completed_coach: false }).eq('id', user.id)

      if (error) {
        console.error('Error resetting onboarding:', error)
      } else {
        setRun(true)
        setStepIndex(0)
      }
    } catch (err) {
      console.error('Error in resetOnboarding:', err)
    }
  }, [user?.id])

  // Start tour manually
  const startTour = useCallback(() => {
    setStepIndex(0)
    setRun(true)
  }, [])

  // Stop tour
  const stopTour = useCallback(() => {
    setRun(false)
  }, [])

  return {
    run,
    steps,
    stepIndex,
    loading,
    setStepIndex,
    setRun,
    completeOnboarding,
    resetOnboarding,
    startTour,
    stopTour,
  }
}
